import React, {useState, useRef, useMemo, useEffect} from 'react';
import {Toast} from "primereact/toast";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {isNullOrUndef} from "chart.js/helpers";
import moment from "moment";
import {Calendar} from "primereact/calendar";
import {BookingsService} from "../../service/BookingsService";
import {Dropdown} from "primereact/dropdown";
import {TradeTypesService} from "../../service/TradeTypesService";
import {UserService} from "../../service/UserService";
import {IncotermsService} from "../../service/IncotermsService";
import {MultiSelect} from "primereact/multiselect";
import {ColumnGroup} from "primereact/columngroup";
import {Row} from "primereact/row";
import {calendarFooterTemplate, todayCalendar} from "../../App";

const STATUS_OPTIONS = [
    {statusId: 2, title: 'New booking'},
    {statusId: 9, title: 'Pre-carriage'},
    {statusId: 1, title: 'SOB'},
    {statusId: 5, title: 'Arrived'},
    {statusId: 10, title: 'On-carriage'},
    {statusId: 8, title: 'Delivered'},
    {statusId: 4, title: 'Closed'},
];

const VIEW_MODE_OPTIONS = [
    {label: 'Rodyti viska', value: 'all'},
    {label: 'Tik kiekius', value: 'raw'},
    {label: 'Tik koef.', value: 'weighted'},
];

export const WorkloadReport = (props) => {

    const [managerList, setManagerList] = useState([])
    const [summary, setSummary] = useState(null)
    const [loading, setLoading] = useState(false);
    const [dateFromTo, setDateFromTo] = useState([]);
    const [bookingDateFromTo, setBookingDateFromTo] = useState([]);
    const [tradeTypeId, setTradeTypeId] = useState(null);
    const [incotermIds, setIncotermIds] = useState([]);
    const [incoterms, setIncoterms] = useState([]);
    const [managers, setManagers] = useState([]);
    const [operators, setOperators] = useState([]);
    const [optionsManagers, setOptionsManagers] = useState([]);
    const [statusIds, setStatusIds] = useState([]);
    const [tradeTypes, setTradeTypes] = useState([]);
    const [viewMode, setViewMode] = useState('all');
    const bookingsService = new BookingsService();
    const tradeTypeService = new TradeTypesService();
    const userService = new UserService();
    const incotermsService = new IncotermsService();
    const toast = useRef(null);
    const dt = useRef(null);
    const dateRangeCalendarRef = useRef(null);
    const dateRangeInputRef = useRef(null);
    const bookingDateCalendarRef = useRef(null);
    const bookingDateInputRef = useRef(null);
    const visibleStatuses = useMemo(() => {
        if (isNullOrUndef(statusIds) || statusIds.length === 0) {
            return STATUS_OPTIONS;
        }
        return statusIds
            .map(id => STATUS_OPTIONS.find(s => s.statusId === id))
            .filter(status => !isNullOrUndef(status));
    }, [statusIds]);
    const showRaw = viewMode !== 'weighted';
    const showWeighted = viewMode !== 'raw';

    useEffect(() => {
        tradeTypeService.getTradeTypes().then(data => {
            setTradeTypes([
                {id: null, name: 'All'},
                ...data.filter(t => t.name !== 'Crosstrade')
            ]);
        });
        incotermsService.getIncoterms().then(data => {
            setIncoterms(data || []);
        });
        userService.getManagers().then(data => setOptionsManagers(data));
    }, [])

    const validateDates = (dateFromTo) => {
        const [start, end] = dateFromTo || [];

        const isValidDate = (d) => d instanceof Date && !isNaN(d);

        if (!isValidDate(start) || !isValidDate(end)) {
            setLoading(false);
            return false
        } else {
            return true;
        }
    }

    const getStatusesByUsers = (exportToExcel) => {
        setLoading(true)
        let validate = false;
        let dateFrom = null;
        let dateTo = null;
        let bookingDateFrom = null;
        let bookingDateTo = null;

        if (!validateDates(bookingDateFromTo) && validateDates(dateFromTo)) {
            validate = true;
            dateFrom = moment(dateFromTo[0]).format('YYYY-MM-DD');
            dateTo = moment(dateFromTo[1]).format('YYYY-MM-DD');
        } else if (!validateDates(dateFromTo) && validateDates(bookingDateFromTo)) {
            validate = true;
            bookingDateFrom = moment(bookingDateFromTo[0]).format('YYYY-MM-DD');
            bookingDateTo = moment(bookingDateFromTo[1]).format('YYYY-MM-DD');
        } else {
            toast.current.show({
                severity: 'warn', summary: 'Date Range', detail: 'Please select a valid Date from to', life: 3000
            });
        }

        if (validate) {
            if (exportToExcel) {
                exportStatusesByUsersToExcel(dateFrom, dateTo, bookingDateFrom, bookingDateTo)
            } else {
                getStatusesByUsersWithParams(dateFrom, dateTo, bookingDateFrom, bookingDateTo)
            }
        }
    }

    const exportStatusesByUsersToExcel = (dateFrom, dateTo, bookingDateFrom, bookingDateTo) => {
        bookingsService.exportToExcelStatusesByUsers(createParams(dateFrom, dateTo, bookingDateFrom, bookingDateTo, true))
            .then(r => {
                if (r.status === 200) {
                    const url = window.URL.createObjectURL(new Blob([r.data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}));
                    const link = document.createElement('a')
                    link.href = url;
                    link.setAttribute('download', 'WorkLoad_' + moment(new Date()).format('YYYY-MM-DD_hh:mm:ss') + '.xlsx');
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
                setLoading(false)
            })
    }

    const getStatusesByUsersWithParams = (dateFrom, dateTo, bookingDateFrom, bookingDateTo) => {
        bookingsService.getStatusesByUsers(createParams(dateFrom, dateTo, bookingDateFrom, bookingDateTo, false))
            .then(r => {
                if(!isNullOrUndef(r)) {
                    setManagerList(Array.isArray(r.data) ? r.data : []);
                    setSummary(!isNullOrUndef(r.summary) ? r.summary : null);
                }
                setLoading(false);
            })
            .catch(() => {
                setManagerList([]);
                setSummary(null);
                setLoading(false);
            });
    }

    const createParams = (dateFrom, dateTo, bookingDateFrom, bookingDateTo, exportXls) => {
        let params = ""
        if (!isNullOrUndef(dateFrom)) {
            params += "dateFrom=" + dateFrom + "&"
        }
        if (!isNullOrUndef(dateTo)) {
            params += "dateTo=" + dateTo + "&"
        }
        if (!isNullOrUndef(bookingDateFrom)) {
            params += "bookingDateFrom=" + bookingDateFrom + "&"
        }
        if (!isNullOrUndef(bookingDateTo)) {
            params += "bookingDateTo=" + bookingDateTo + "&"
        }
        if (!isNullOrUndef(tradeTypeId)) {
            params += "tradeTypeId=" + tradeTypeId + "&"
        }
        if (!isNullOrUndef(incotermIds) && incotermIds.length > 0) {
            incotermIds.forEach(i => {
                params += "incotermIds=" + i + "&"
            })
        }
        if (!isNullOrUndef(managers) && managers.length > 0) {
            managers.forEach(m => {
                params += "managerIds=" + m.id + "&"
            })
        }
        if (!isNullOrUndef(operators) && operators.length > 0) {
            operators.forEach(m => {
                params += "operatorIds=" + m.id + "&"
            })
        }
        if (!isNullOrUndef(statusIds) && statusIds.length > 0) {
            statusIds.forEach(s => {
                params += "statusIds=" + s + "&"
            })
        }
        if (exportXls && !isNullOrUndef(statusIds) && statusIds.length > 0) {
            params += "columns=operator," + statusIds.map(s => 'status.'+s).join(",") + ",total"
        }

        return params;
    }

    const clearFilters = () => {
        setManagerList([])
        setManagers([])
        setSummary(null)
        setDateFromTo([])
        setBookingDateFromTo([])
        setStatusIds([])
        setIncotermIds([])
        setTradeTypeId(null)
        setOperators([])
    }

    const getRawCount = (statusMap, statusId) => {
        if (isNullOrUndef(statusMap)) {
            return 0;
        }
        return !isNullOrUndef(statusMap[statusId]) ? statusMap[statusId] : 0;
    }

    const getWeightedCount = (statusMap, statusId) => {
        if (isNullOrUndef(statusMap)) {
            return '0.0000';
        }
        const value = !isNullOrUndef(statusMap[statusId]) ? statusMap[statusId] : 0;
        return Number(value).toFixed(4);
    }

    const header = <>
        <div className="table-header">
            <h5 className="p-mb-2">Workload</h5>
        </div>
        <div className="flex justify-content-between card-container">
            <div style={{flex: 1, minWidth: 0}}>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '0.75rem'}}>
                    <span className="p-float-label">
                        <Calendar id={'filter-dateFromTo'} selectionMode="range" numberOfMonths={3}
                                  inputRef={dateRangeInputRef} ref={dateRangeCalendarRef}
                                  viewDate={todayCalendar(dateFromTo[0])}
                                  footerTemplate={() => calendarFooterTemplate(dateRangeCalendarRef, dateRangeInputRef)}
                                  dateFormat={'yy-mm-dd'} locale={'lt'} style={{width: '100%'}}
                                  value={dateFromTo} onChange={e => {
                            setDateFromTo(e.value);
                            setBookingDateFromTo([]);
                        }}/>
                        <label htmlFor={'filter-dateFromTo'}>{'Date From To'}</label>
                    </span>
                    <span className="p-float-label">
                        <Calendar id={'filter-bookingDateFromTo'} selectionMode="range" numberOfMonths={3}
                                  inputRef={bookingDateInputRef} ref={bookingDateCalendarRef}
                                  viewDate={todayCalendar(bookingDateFromTo[0])}
                                  footerTemplate={() => calendarFooterTemplate(bookingDateCalendarRef, bookingDateInputRef)}
                                  dateFormat={'yy-mm-dd'} locale={'lt'} style={{width: '100%'}}
                                  value={bookingDateFromTo} onChange={e => {
                            setBookingDateFromTo(e.value);
                            setDateFromTo([]);
                        }}/>
                        <label htmlFor={'filter-bookingDateFromTo'}>{'Booking ID'}</label>
                    </span>
                    <span className="p-float-label">
                        <Dropdown id={'filter-tradeTypes'} value={tradeTypeId} options={tradeTypes}
                                  onChange={(e) => setTradeTypeId(e.target.value)} optionLabel="name"
                                  optionValue={'id'} placeholder="-- All --" style={{width: '100%'}}/>
                        <label htmlFor={`filter-tradeTypes`}>Imp/Exp</label>
                    </span>
                    <span className="p-float-label">
                        <MultiSelect id={'filter-incotermIds'} value={incotermIds} options={incoterms}
                                     maxSelectedLabels={0} placeholder="Incoterms"
                                     optionLabel={'title'} optionValue={'id'} style={{width: '100%'}}
                                     onHide={() => getStatusesByUsers(false)}
                                     onChange={(e) => setIncotermIds(e.value)}/>
                        <label htmlFor={`filter-incotermIds`}>Incoterms</label>
                    </span>
                    <span className="p-float-label">
                        <MultiSelect id={'filter-managerIds'} value={managers} options={optionsManagers}
                                     maxSelectedLabels={0} placeholder="Select Manager"
                                     itemTemplate={m => `${m.firstName} ${m.lastName}`}
                                     onChange={(e) => setManagers(e.value)}
                                     style={{width: '100%'}}/>
                        <label htmlFor={`filter-managerIds`}>Manager</label>
                    </span>
                    <span className="p-float-label">
                        <MultiSelect id={'filter-operatorsIds'} value={operators} options={optionsManagers}
                                     maxSelectedLabels={0} placeholder="Select Operator"
                                     itemTemplate={m => `${m.firstName} ${m.lastName}`}
                                     onChange={(e) => setOperators(e.value)}
                                     style={{width: '100%'}}/>
                        <label htmlFor={`filter-operatorsIds`}>Operators</label>
                    </span>
                    <span className="p-float-label">
                        <MultiSelect id={'filter-statusId'} value={statusIds} options={STATUS_OPTIONS}
                                     maxSelectedLabels={0} placeholder="Status"
                                     optionLabel={'title'} optionValue={'statusId'} style={{width: '100%'}}
                                     onHide={() => getStatusesByUsers(false)}
                                     onChange={(e) => setStatusIds(e.value)}/>
                        <label htmlFor={`filter-statusId`}>Status</label>
                    </span>
                    <span className="p-float-label">
                        <Dropdown id={'filter-viewMode'} value={viewMode} options={VIEW_MODE_OPTIONS}
                                  optionLabel="label" optionValue="value" style={{width: '100%'}}
                                  onChange={(e) => setViewMode(e.value)} placeholder="Rodyti viska"/>
                        <label htmlFor={`filter-viewMode`}>Rodymas</label>
                    </span>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <span className={'p-buttonset'}>
                            <Button icon="pi pi-search" iconPos="right"
                                    onClick={() => getStatusesByUsers(false)}/>
                            <Button icon="pi pi-times" iconPos="right" onClick={() => clearFilters()}/>
                        </span>
                    </div>
                </div>
            </div>
            <div style={{marginLeft: '0.75rem', display: 'flex', alignItems: 'flex-start'}}>
                <Button icon="pi pi-file-excel" iconPos="right" label={'Export'}
                        onClick={() => getStatusesByUsers(true)}/>
            </div>
        </div>
    </>;

    const managerTemplate = (rowData) => {
        if (!isNullOrUndef(rowData)) {
            return rowData.operator.firstName + ' ' + rowData.operator.lastName
        } else {
            return ''
        }
    }

    const getStatusesColumn = () => {
        let statusOptions_ = [];
        visibleStatuses.forEach(status => {
            if (showRaw) {
                statusOptions_.push(<Column sortable={true} key={'raw-' + status.statusId}
                                            field={'statusCounts.' + [status.statusId]}
                                            header={status.title}
                                            body={(rowData) => getRawCount(rowData.statusCounts, status.statusId)}/>)
            }
            if (showWeighted) {
                statusOptions_.push(<Column sortable={true} key={'weighted-' + status.statusId}
                                            field={'weightedStatusCounts.' + [status.statusId]}
                                            header={`${status.title} (%)`}
                                            body={(rowData) => getWeightedCount(rowData.weightedStatusCounts, status.statusId)}/>)
            }
        });
        return statusOptions_;
    }

    const getFooterSColumn = () => {
        let statusOptions_ = [];
        visibleStatuses.forEach(status => {
            if (showRaw) {
                statusOptions_.push(<Column key={'footer-raw-' + status.statusId} field={'footerRawColumn_' + status.statusId}
                                            footer={getRawCount(summary.statusCounts, status.statusId)}/>)
            }
            if (showWeighted) {
                statusOptions_.push(<Column key={'footer-weighted-' + status.statusId} field={'footerWeightedColumn_' + status.statusId}
                                            footer={getWeightedCount(summary.weightedStatusCounts, status.statusId)}/>)
            }
        });
        return statusOptions_;
    }

    const footerGroup = () => {
        return <ColumnGroup>
            <Row>
                <Column footer={'TOTAL'} style={{fontWeight: 'bold'}}/>
                {!isNullOrUndef(summary) ? getFooterSColumn() : null}
                {showRaw ? <Column footer={!isNullOrUndef(summary) ? summary.totalCount : '-'}/> : null}
                {showWeighted ? <Column footer={!isNullOrUndef(summary) ? Number(summary.weightedTotalCount || 0).toFixed(4) : '-'}/> : null}
            </Row>
        </ColumnGroup>
    }

    return (<div className="p-grid" style={{margin: '0 -20px'}}>
        <div className="p-col-12">
            <Toast ref={toast}/>
            <div style={{height: 'calc(100vh - 115px)', fontSize: '15px'}}>
                <DataTable ref={dt} value={managerList}
                           size="small"
                           footerColumnGroup={footerGroup()}
                           dataKey="operator.id"
                           scrollable rows={managerList.length}
                           responsiveLayout="scroll"
                           scrollHeight="flex" id={'issuedEnteredInvoices-list'}
                           stateKey="issued-entered-invoices-dt-state-local"
                           className="datatable-responsive" loading={loading} rowHover
                           emptyMessage="No entry found." header={header}>
                    <Column sortable={true} field={'operator.firstName'} header={'Operator'}
                            body={rowData => managerTemplate(rowData)}/>
                    {getStatusesColumn()}
                    {showRaw ? <Column sortable={true} field={'totalCount'} header={'Total'}
                                       body={rowData => !isNullOrUndef(rowData.totalCount) ? rowData.totalCount : 0}/> : null}
                    {showWeighted ? <Column sortable={true} field={'weightedTotalCount'} header={'Total (%)'}
                                            body={rowData => Number(rowData.weightedTotalCount || 0).toFixed(4)}/> : null}
                </DataTable>
            </div>
        </div>
    </div>);

}
